import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';

const NotificationContext = createContext(null);

// Global ref for imperative toast API (toast.success(...), etc.)
let globalNotificationHandler = null;

export const toast = {
  success: (message, title, duration) => {
    if (globalNotificationHandler) {
      globalNotificationHandler.showSuccess(message, title, duration);
    } else {
      console.log('[Toast Success]', message);
    }
  },
  error: (message, title, duration) => {
    if (globalNotificationHandler) {
      globalNotificationHandler.showError(message, title, duration);
    } else {
      console.error('[Toast Error]', message);
    }
  },
  warning: (message, title, duration) => {
    if (globalNotificationHandler) {
      globalNotificationHandler.showWarning(message, title, duration);
    } else {
      console.warn('[Toast Warning]', message);
    }
  },
  info: (message, title, duration) => {
    if (globalNotificationHandler) {
      globalNotificationHandler.showInfo(message, title, duration);
    } else {
      console.info('[Toast Info]', message);
    }
  },
  notify: (options) => {
    if (globalNotificationHandler) {
      globalNotificationHandler.notify(options);
    }
  }
};

const TOAST_THEMES = {
  success: {
    border: 'border-emerald-200/90',
    bg: 'bg-white',
    gradient: 'from-emerald-500 to-teal-600',
    iconBg: 'bg-emerald-100/80 text-emerald-600',
    icon: CheckCircle2,
    defaultTitle: 'Success',
    badge: 'bg-emerald-100 text-emerald-800',
    progressBar: 'bg-gradient-to-r from-emerald-500 to-teal-500'
  },
  error: {
    border: 'border-rose-200/90',
    bg: 'bg-white',
    gradient: 'from-rose-500 to-red-600',
    iconBg: 'bg-rose-100/80 text-rose-600',
    icon: AlertCircle,
    defaultTitle: 'Error',
    badge: 'bg-rose-100 text-rose-800',
    progressBar: 'bg-gradient-to-r from-rose-500 to-red-600'
  },
  warning: {
    border: 'border-amber-200/90',
    bg: 'bg-white',
    gradient: 'from-amber-400 to-orange-500',
    iconBg: 'bg-amber-100/80 text-amber-600',
    icon: AlertTriangle,
    defaultTitle: 'Warning',
    badge: 'bg-amber-100 text-amber-800',
    progressBar: 'bg-gradient-to-r from-amber-400 to-orange-500'
  },
  info: {
    border: 'border-blue-200/90',
    bg: 'bg-white',
    gradient: 'from-blue-500 to-indigo-600',
    iconBg: 'bg-blue-100/80 text-blue-600',
    icon: Info,
    defaultTitle: 'Notice',
    badge: 'bg-blue-100 text-blue-800',
    progressBar: 'bg-gradient-to-r from-blue-500 to-indigo-600'
  }
};

const ToastItem = ({ notification, onDismiss }) => {
  const { id, type = 'info', title, message, duration = 4500 } = notification;
  const theme = TOAST_THEMES[type] || TOAST_THEMES.info;
  const IconComponent = theme.icon;

  const [progress, setProgress] = useState(100);
  const isPausedRef = useRef(false);

  useEffect(() => {
    if (duration <= 0) return;
    const intervalMs = 25;
    const totalTicks = duration / intervalMs;
    let ticksPassed = 0;

    const timer = setInterval(() => {
      if (!isPausedRef.current) {
        ticksPassed++;
        const pct = Math.max(0, 100 - (ticksPassed / totalTicks) * 100);
        setProgress(pct);
        if (ticksPassed >= totalTicks) {
          clearInterval(timer);
          onDismiss(id);
        }
      }
    }, intervalMs);

    return () => clearInterval(timer);
  }, [id, duration, onDismiss]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -16, scale: 0.94, x: 24 }}
      animate={{ opacity: 1, y: 0, scale: 1, x: 0 }}
      exit={{ opacity: 0, scale: 0.92, x: 30, transition: { duration: 0.18 } }}
      transition={{ type: 'spring', stiffness: 450, damping: 32 }}
      onMouseEnter={() => { isPausedRef.current = true; }}
      onMouseLeave={() => { isPausedRef.current = false; }}
      className={`relative w-full overflow-hidden rounded-2xl border ${theme.border} bg-white shadow-2xl shadow-slate-900/15 transition-all duration-200 pointer-events-auto flex flex-col group`}
    >
      <div className="flex items-start p-4 gap-3.5">
        {/* Color accent left bar */}
        <div className={`absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b ${theme.gradient}`} />

        {/* Icon */}
        <div className={`p-2.5 rounded-xl ${theme.iconBg} flex-shrink-0 shadow-xs flex items-center justify-center`}>
          <IconComponent className="w-5 h-5" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 pr-6">
          <div className="flex items-center gap-2 mb-0.5">
            <h4 className="text-sm font-bold text-gray-900 leading-tight">
              {title || theme.defaultTitle}
            </h4>
          </div>
          <div className="text-xs text-gray-600 leading-relaxed font-medium break-words">
            {message}
          </div>
        </div>

        {/* Close Button */}
        <button
          onClick={() => onDismiss(id)}
          className="absolute top-3.5 right-3.5 p-1 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
          title="Dismiss notification"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Animated countdown progress bar */}
      {duration > 0 && (
        <div className="w-full h-1 bg-gray-100 overflow-hidden">
          <div
            className={`h-full transition-all duration-75 ease-linear ${theme.progressBar}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </motion.div>
  );
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const dismiss = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const notify = useCallback(({ type = 'info', message, title, duration = 4500 }) => {
    if (!message) return;
    const id = Date.now() + Math.random().toString(36).substring(2, 9);
    setNotifications((prev) => [
      ...prev,
      { id, type, message, title, duration }
    ]);
    return id;
  }, []);

  const showSuccess = useCallback((message, title = 'Success', duration = 4500) => {
    return notify({ type: 'success', message, title, duration });
  }, [notify]);

  const showError = useCallback((message, title = 'Action Failed', duration = 5000) => {
    return notify({ type: 'error', message, title, duration });
  }, [notify]);

  const showWarning = useCallback((message, title = 'Attention', duration = 5000) => {
    return notify({ type: 'warning', message, title, duration });
  }, [notify]);

  const showInfo = useCallback((message, title = 'Notice', duration = 4500) => {
    return notify({ type: 'info', message, title, duration });
  }, [notify]);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  // Sync global imperative toast handler
  useEffect(() => {
    globalNotificationHandler = {
      notify,
      showSuccess,
      showError,
      showWarning,
      showInfo,
      dismiss,
      clearAll
    };

    // Listen to custom DOM events for legacy compatibility
    const handleCustomToastEvent = (e) => {
      if (e?.detail) {
        notify(e.detail);
      }
    };
    window.addEventListener('app-notification', handleCustomToastEvent);

    return () => {
      window.removeEventListener('app-notification', handleCustomToastEvent);
      globalNotificationHandler = null;
    };
  }, [notify, showSuccess, showError, showWarning, showInfo, dismiss, clearAll]);

  const contextValue = {
    notifications,
    notify,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    dismiss,
    clearAll
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}

      {/* Floating Pop-up Toast Container */}
      <div
        aria-live="polite"
        className="fixed top-20 right-6 z-[99999] flex flex-col space-y-3 pointer-events-none max-w-sm sm:max-w-md w-full px-4 sm:px-0"
      >
        <AnimatePresence mode="popLayout">
          {notifications.map((notification) => (
            <ToastItem
              key={notification.id}
              notification={notification}
              onDismiss={dismiss}
            />
          ))}
        </AnimatePresence>
      </div>
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    // Graceful fallback if invoked outside provider
    return {
      showSuccess: toast.success,
      showError: toast.error,
      showWarning: toast.warning,
      showInfo: toast.info,
      notify: toast.notify,
      dismiss: () => {},
      clearAll: () => {}
    };
  }
  return context;
};

// Aliases for convenience
export const useToast = useNotification;
